import type { CurrentUser } from '@zhiyue/core';
import {
  listOrganizationMembers,
  type OrganizationMember,
} from '@/services/bff/members.api';
import { fetchOpenPlatformToken } from '@/services/openPlatformToken';

type MemberDirectoryUser = Pick<CurrentUser, 'tenantUuid' | 'memberUuid'> & {
  organizationUuid?: string;
};

let identityKey = '';
let loading: Promise<void> | null = null;
const members = new Map<string, OrganizationMember>();

const getIdentityKey = (currentUser?: Partial<MemberDirectoryUser> | null) =>
  [
    currentUser?.tenantUuid,
    currentUser?.organizationUuid,
    currentUser?.memberUuid,
  ]
    .map((value) => String(value || '').trim())
    .join('|');

export const clearMemberDirectory = () => {
  identityKey = '';
  loading = null;
  members.clear();
};

export const loadMemberDirectory = async (
  currentUser?: Partial<MemberDirectoryUser> | null,
): Promise<void> => {
  const nextIdentityKey = getIdentityKey(currentUser);
  const memberUuid = String(currentUser?.memberUuid || '').trim();
  if (!nextIdentityKey || !memberUuid) return clearMemberDirectory();
  if (identityKey === nextIdentityKey && (loading || members.size))
    return loading || undefined;

  identityKey = nextIdentityKey;
  members.clear();
  loading = fetchOpenPlatformToken(currentUser as MemberDirectoryUser)
    .then(({ accessToken }) => listOrganizationMembers(accessToken, memberUuid))
    .then((items) => {
      if (identityKey !== nextIdentityKey) return;
      items.forEach((member) => {
        const nextMemberUuid = member.memberUuid || member.id;
        if (nextMemberUuid) members.set(nextMemberUuid, member);
      });
    })
    .finally(() => {
      if (identityKey === nextIdentityKey) loading = null;
    });
  return loading;
};

export const getMemberName = (memberUuid?: string | null): string => {
  if (!memberUuid) return '-';
  return members.get(memberUuid)?.name || memberUuid;
};
